import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './parallelCoordinates.Renderer';
import { convertColumnDataToDisplayString } from '../../helpers/misc';

export function ParallelCoordinates({
  chartTypeManifest,
  dataContext,
  dataBinding,
  config,
  widgetContext,
}: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    dataTable,
    colInfoHover,
    dataHoverLabel,
    dataItemId,
    coordinates,
    commonAxes,
    areaLines,
    getItemIndexById,
    customHoverRenderer,
    title,
    getHorzCoordValue,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'lines') return null;
    if (coordinates.length === 0) return null;
    let minDist = mappingInfo.mapsize(10);
    let coordIdx = null;
    let itemId = null;
    let hoverCentX = null;
    let hoverCentY = null;

    // Find coordinate axis for the hover position based on logicPositX (which is between 0 and 1)
    const boundLogicX = Math.max(0, Math.min(1, logicPositX));
    const coordinateLoc = Math.min(Math.trunc(boundLogicX * coordinates.length), coordinates.length - 1);
    const coord = coordinates[coordinateLoc];

    for (let rowNr = 0; rowNr < dataItemId.length; rowNr++) {
      const value = coord.data[rowNr];
      if (!isNaN(value) && (coord.maxVal > coord.minVal || commonAxes)) {
        let valueCorr = value;
        if (!commonAxes) valueCorr = (value - coord.minVal) / (coord.maxVal - coord.minVal);
        const hoverCenterX = getHorzCoordValue(rowNr, coordinateLoc);
        const scr_x = mappingInfo.mapX.map(hoverCenterX);
        const scr_y = mappingInfo.mapY.map(valueCorr);
        const dst = Math.abs(scr_x - renderPositX) + Math.abs(scr_y - renderPositY);
        if (dst <= minDist) {
          minDist = dst;
          itemId = dataItemId[rowNr];
          hoverCentX = hoverCenterX;
          hoverCentY = valueCorr;
          coordIdx = coordinateLoc;
        }
      }
    }
    if (itemId == null) return null;
    return {
      dataTableId: dataBinding.dataTableId,
      itemId,
      custom: { hoverCentX, hoverCentY, coordIdx },
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId) return null;
    if (hoverInfo.itemId == null) return null;
    const hoverItemNr = getItemIndexById(hoverInfo.itemId);
    if (hoverItemNr === undefined) return null;
    const labels: string[] = [];
    if (context.isActiveChart) {
      if (dataHoverLabel) labels.push(convertColumnDataToDisplayString(colInfoHover, dataHoverLabel[hoverItemNr]));
      if (hoverInfo.custom?.coordIdx !== undefined) {
        const coord = coordinates[hoverInfo.custom!.coordIdx];
        labels.push(
          `${coord.columnInfo.name}=${convertColumnDataToDisplayString(coord.columnInfo, coord.data[hoverItemNr])}`,
        );
      }
    }
    return {
      area: areaLines,
      logicPosX: context.isActiveChart ? hoverInfo.custom?.hoverCentX : null,
      logicPosY: context.isActiveChart ? hoverInfo.custom?.hoverCentY : null,
      labels,
      customHoverRenderer,
    };
  }

  function onItemClicked(itemId: string) {
    widgetContext.handleOpenItem(dataTable.canonicalTypeId, itemId);
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onItemClicked={onItemClicked}
        maxZoomXRelative={30}
        maxZoomYRelative={30}
        widgetContext={widgetContext}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
